/**
 * Admin — Website Analytics
 *
 * First-party analytics for the marketing site (4klabs.ai) and app sign-ups: traffic,
 * channels (AI assistants called out), pages, organizations, the homepage demo, the
 * visitor → sign-up funnel and recent leads with their first touch. A Visitors tab
 * lists recent visitors; click one for its event timeline.
 *
 * Reads GET /api/admin/web-analytics/summary and /visitors(/:id). Those endpoints ship
 * with the API separately — a 404 renders an explanatory empty state, not an error.
 */

import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn } from '../../lib/utils';

export const Route = createFileRoute('/admin/web-analytics')({
  component: AdminWebAnalytics,
});

// ---------------------------------------------------------------------------
// Shapes (GET /api/admin/web-analytics/*)
// ---------------------------------------------------------------------------

interface TouchLike {
  ch?: string;
  src?: string;
  med?: string;
  cmp?: string;
  lp?: string;
  ref?: string;
  ts?: number;
}

interface Summary {
  range: { from: string; to: string; days: number } | null;
  totals: { pageViews: number; visitors: number; engagedVisitors: number; demoRequests: number; signups: number };
  daily: Array<{ date: string; pageViews: number; visitors: number }>;
  channels: Array<{ channel: string; visitors: number; pageViews: number; demoRequests: number }>;
  sources: Array<{ source: string; medium: string; visitors: number }>;
  pages: Array<{ path: string; pageViews: number; visitors: number; avgEngagedMs: number }>;
  landingPages: Array<{ path: string; visitors: number }>;
  countries: Array<{ country: string; visitors: number }>;
  organizations: Array<{ org: string; domain: string | null; visitors: number; pageViews: number; lastSeen: string }>;
  demoSteps: Array<{ demo: string; text: string; count: number }>;
  funnel: { visitors: number; engaged: number; demoInteracted: number; formStarted: number; demoRequested: number; signedUp: number };
  recentLeads: Array<{
    id: string;
    createdAt: string;
    fullName: string;
    workEmail: string;
    companyName: string;
    source: string | null;
    channel: string | null;
    visitorId: string | null;
    firstTouch: TouchLike | null;
    pageViewsBefore: number | null;
  }>;
}

interface VisitorRow {
  visitorId: string;
  kind: string;
  firstSeen: string;
  lastSeen: string;
  pageViews: number;
  country: string | null;
  org: string | null;
  domain: string | null;
  channel: string | null;
  firstTouch: TouchLike | null;
  email: string | null;
  userId: string | null;
}

interface VisitorDetail {
  visitor: VisitorRow | null;
  events: Array<{ ts: string | number; event: string; path: string | null; props: unknown; channel: string | null }>;
}

const EMPTY_TOTALS: Summary['totals'] = { pageViews: 0, visitors: 0, engagedVisitors: 0, demoRequests: 0, signups: 0 };
const EMPTY_FUNNEL: Summary['funnel'] = { visitors: 0, engaged: 0, demoInteracted: 0, formStarted: 0, demoRequested: 0, signedUp: 0 };

const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** The API ships separately; tolerate missing fields instead of crashing the page. */
function normalizeSummary(raw: unknown): Summary {
  const r = (raw ?? {}) as Partial<Summary>;
  return {
    range: r.range ?? null,
    totals: { ...EMPTY_TOTALS, ...(r.totals ?? {}) },
    daily: arr(r.daily),
    channels: arr(r.channels),
    sources: arr(r.sources),
    pages: arr(r.pages),
    landingPages: arr(r.landingPages),
    countries: arr(r.countries),
    organizations: arr(r.organizations),
    demoSteps: arr(r.demoSteps),
    funnel: { ...EMPTY_FUNNEL, ...(r.funnel ?? {}) },
    recentLeads: arr(r.recentLeads),
  };
}

type LoadError = { kind: 'missing' } | { kind: 'error'; message: string };

function toLoadError(err: unknown, fallback: string): LoadError {
  const e = err as { response?: { status?: number; data?: { error?: string | { message?: string }; message?: string } }; message?: string };
  if (e?.response?.status === 404) return { kind: 'missing' };
  const d = e?.response?.data;
  const msg = (typeof d?.error === 'string' ? d.error : d?.error?.message) || d?.message || e?.message || fallback;
  return { kind: 'error', message: msg };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const fmt = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-US') : '—');

function pct(part: number, whole: number): string {
  if (!whole) return '—';
  const p = (part / whole) * 100;
  return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1)}%`;
}

function duration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

function when(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function shortDay(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

const CHANNEL_LABEL: Record<string, string> = {
  ai_assistant: 'AI assistant',
  organic_search: 'Organic search',
  paid_search: 'Paid search',
  paid_social: 'Paid social',
  social: 'Social',
  email: 'Email',
  referral: 'Referral',
  internal: 'Internal',
  direct: 'Direct',
};

function ChannelLabel({ channel }: { channel: string | null | undefined }) {
  if (!channel) return <span className="text-muted-foreground">—</span>;
  const label = CHANNEL_LABEL[channel] ?? channel;
  if (channel === 'ai_assistant') {
    return (
      <Badge className="gap-1 whitespace-nowrap">
        <Sparkles className="h-3 w-3" />
        {label}
      </Badge>
    );
  }
  return <span className="whitespace-nowrap">{label}</span>;
}

function touchText(t: TouchLike | null | undefined): string {
  if (!t) return '—';
  const src = t.src && t.src !== '(direct)' ? t.src : null;
  return [src, t.cmp ? `“${t.cmp}”` : null].filter(Boolean).join(' · ') || '—';
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Kpi({ title, value, hint }: { title: string; value: number; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold tabular-nums">{fmt(value)}</div>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function Section({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Empty({ children = 'No data in this range.' }: { children?: ReactNode }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  numeric?: boolean;
  className?: string;
}

function DataTable<T>({ rows, columns, rowKey, rowClassName }: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T, i: number) => string;
  rowClassName?: (row: T) => string | undefined;
}) {
  if (rows.length === 0) return <Empty />;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((c) => (
            <TableHead key={c.header} className={cn(c.numeric && 'text-right', c.className)}>{c.header}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={rowKey(r, i)} className={rowClassName?.(r)}>
            {columns.map((c) => (
              <TableCell key={c.header} className={cn('text-sm', c.numeric && 'text-right tabular-nums', c.className)}>
                {c.cell(r)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Visitors per day — one series, so no legend; hover a bar for its numbers. */
function DailyChart({ daily }: { daily: Summary['daily'] }) {
  const [hover, setHover] = useState<number | null>(null);
  if (daily.length === 0) return <Empty />;
  const max = Math.max(1, ...daily.map((d) => d.visitors || 0));
  const h = hover !== null ? daily[hover] : null;
  const labelEvery = Math.max(1, Math.ceil(daily.length / 8));
  return (
    <div>
      <div className="mb-2 h-5 text-xs text-muted-foreground" aria-live="polite">
        {h ? (
          <span>
            <span className="font-medium text-foreground">{shortDay(h.date)}</span> · {fmt(h.visitors)} visitors · {fmt(h.pageViews)} page views
          </span>
        ) : (
          <span>Peak {fmt(max)} visitors/day</span>
        )}
      </div>
      <div
        className="flex h-40 items-end gap-[2px] border-b border-border"
        role="img"
        aria-label={`Visitors per day, ${daily.length} days, peak ${max}`}
        onMouseLeave={() => setHover(null)}
      >
        {daily.map((d, i) => (
          <div
            key={d.date}
            className="flex h-full flex-1 items-end"
            onMouseEnter={() => setHover(i)}
            title={`${shortDay(d.date)}: ${d.visitors} visitors, ${d.pageViews} page views`}
          >
            <div
              className={cn(
                'w-full rounded-t-[4px] transition-colors',
                hover === i ? 'bg-primary' : 'bg-primary/70',
              )}
              style={{ height: `${d.visitors > 0 ? Math.max(2, (d.visitors / max) * 100) : 0}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-[2px] text-[10px] text-muted-foreground">
        {daily.map((d, i) => (
          <div key={d.date} className="flex-1 overflow-visible whitespace-nowrap text-center">
            {i % labelEvery === 0 ? shortDay(d.date) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function Funnel({ funnel }: { funnel: Summary['funnel'] }) {
  const steps: Array<[string, number]> = [
    ['Visitors', funnel.visitors],
    ['Engaged', funnel.engaged],
    ['Interacted with demo', funnel.demoInteracted],
    ['Started a form', funnel.formStarted],
    ['Requested a demo', funnel.demoRequested],
    ['Signed up', funnel.signedUp],
  ];
  const top = Math.max(1, funnel.visitors);
  if (!funnel.visitors) return <Empty />;
  return (
    <div className="space-y-2">
      {steps.map(([label, n], i) => (
        <div key={label} className="grid grid-cols-[10rem_1fr_7rem] items-center gap-3 text-sm">
          <span className="text-muted-foreground">{label}</span>
          <div className="h-5 rounded bg-muted">
            <div className="h-full rounded bg-primary/70" style={{ width: `${Math.min(100, (n / top) * 100)}%` }} />
          </div>
          <span className="text-right tabular-nums">
            {fmt(n)}
            {i > 0 && <span className="ml-1 text-xs text-muted-foreground">({pct(n, steps[i - 1][1])})</span>}
          </span>
        </div>
      ))}
      <p className="pt-1 text-xs text-muted-foreground">Percent = conversion from the step above.</p>
    </div>
  );
}

function MissingApi() {
  return (
    <Card>
      <CardContent className="py-10 text-center text-sm text-muted-foreground">
        The website analytics API isn't available on this server yet (404). This page fills in once the API change
        that adds <code className="font-mono">/api/admin/web-analytics</code> is deployed.
      </CardContent>
    </Card>
  );
}

function ErrorBox({ message }: { message: string }) {
  return <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{message}</div>;
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function Overview({ s }: { s: Summary }) {
  const t = s.totals;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi title="Page views" value={t.pageViews} />
        <Kpi title="Visitors" value={t.visitors} />
        <Kpi title="Engaged visitors" value={t.engagedVisitors} hint={`${pct(t.engagedVisitors, t.visitors)} of visitors`} />
        <Kpi title="Demo requests" value={t.demoRequests} />
        <Kpi title="Sign-ups" value={t.signups} />
      </div>

      <Section title="Daily visitors">
        <DailyChart daily={s.daily} />
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Channels">
          <DataTable
            rows={s.channels}
            rowKey={(r) => r.channel}
            rowClassName={(r) => (r.channel === 'ai_assistant' ? 'bg-primary/5' : undefined)}
            columns={[
              { header: 'Channel', cell: (r) => <ChannelLabel channel={r.channel} /> },
              { header: 'Visitors', numeric: true, cell: (r) => fmt(r.visitors) },
              { header: 'Page views', numeric: true, cell: (r) => fmt(r.pageViews) },
              { header: 'Demo requests', numeric: true, cell: (r) => fmt(r.demoRequests) },
            ]}
          />
        </Section>

        <Section title="Visitor → sign-up funnel">
          <Funnel funnel={s.funnel} />
        </Section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Top sources">
          <DataTable
            rows={s.sources}
            rowKey={(r, i) => `${r.source}|${r.medium}|${i}`}
            columns={[
              { header: 'Source', cell: (r) => r.source || '—' },
              { header: 'Medium', cell: (r) => <span className="text-muted-foreground">{r.medium || '—'}</span> },
              { header: 'Visitors', numeric: true, cell: (r) => fmt(r.visitors) },
            ]}
          />
        </Section>
        <Section title="Landing pages">
          <DataTable
            rows={s.landingPages}
            rowKey={(r) => r.path}
            columns={[
              { header: 'Page', cell: (r) => <span className="font-mono text-xs">{r.path}</span> },
              { header: 'Visitors', numeric: true, cell: (r) => fmt(r.visitors) },
            ]}
          />
        </Section>
      </div>

      <Section title="Top pages">
        <DataTable
          rows={s.pages}
          rowKey={(r) => r.path}
          columns={[
            { header: 'Page', cell: (r) => <span className="font-mono text-xs">{r.path}</span> },
            { header: 'Page views', numeric: true, cell: (r) => fmt(r.pageViews) },
            { header: 'Visitors', numeric: true, cell: (r) => fmt(r.visitors) },
            { header: 'Avg. engaged', numeric: true, cell: (r) => duration(r.avgEngagedMs) },
          ]}
        />
      </Section>

      <div className="grid gap-6 lg:grid-cols-3">
        <Section title="Countries">
          <DataTable
            rows={s.countries}
            rowKey={(r) => r.country}
            columns={[
              { header: 'Country', cell: (r) => r.country || 'Unknown' },
              { header: 'Visitors', numeric: true, cell: (r) => fmt(r.visitors) },
            ]}
          />
        </Section>
        <Section title="Organizations" className="lg:col-span-2">
          <DataTable
            rows={s.organizations}
            rowKey={(r, i) => `${r.org}|${r.domain}|${i}`}
            columns={[
              {
                header: 'Organization',
                cell: (r) => (
                  <div>
                    <div className="font-medium">{r.org || '—'}</div>
                    {r.domain && <div className="text-xs text-muted-foreground">{r.domain}</div>}
                  </div>
                ),
              },
              { header: 'Visitors', numeric: true, cell: (r) => fmt(r.visitors) },
              { header: 'Page views', numeric: true, cell: (r) => fmt(r.pageViews) },
              { header: 'Last seen', className: 'whitespace-nowrap', cell: (r) => <span className="text-muted-foreground">{when(r.lastSeen)}</span> },
            ]}
          />
        </Section>
      </div>

      <Section title="Homepage demo steps">
        <DataTable
          rows={s.demoSteps}
          rowKey={(r, i) => `${r.demo}|${r.text}|${i}`}
          columns={[
            { header: 'Demo', cell: (r) => <span className="text-muted-foreground">{r.demo}</span> },
            { header: 'Step', cell: (r) => r.text },
            { header: 'Count', numeric: true, cell: (r) => fmt(r.count) },
          ]}
        />
      </Section>

      <Section title="Recent leads">
        <DataTable
          rows={s.recentLeads}
          rowKey={(r) => r.id}
          columns={[
            { header: 'When', className: 'whitespace-nowrap', cell: (r) => <span className="text-muted-foreground">{when(r.createdAt)}</span> },
            {
              header: 'Lead',
              cell: (r) => (
                <div>
                  <div className="font-medium">{r.fullName}</div>
                  <a href={`mailto:${r.workEmail}`} className="text-xs text-primary hover:underline">{r.workEmail}</a>
                </div>
              ),
            },
            { header: 'Company', cell: (r) => r.companyName || '—' },
            { header: 'First touch', cell: (r) => <ChannelLabel channel={r.firstTouch?.ch ?? r.channel} /> },
            { header: 'Source', cell: (r) => <span className="text-xs">{touchText(r.firstTouch)}</span> },
            { header: 'Landing page', cell: (r) => <span className="font-mono text-xs">{r.firstTouch?.lp ?? '—'}</span> },
            { header: 'Views before', numeric: true, cell: (r) => fmt(r.pageViewsBefore) },
          ]}
        />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visitors
// ---------------------------------------------------------------------------

function VisitorTimeline({ visitorId }: { visitorId: string }) {
  const [detail, setDetail] = useState<VisitorDetail | null>(null);
  const [error, setError] = useState<LoadError | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    api
      .get(`/admin/web-analytics/visitors/${encodeURIComponent(visitorId)}`)
      .then((r) => {
        if (cancelled) return;
        const d = (r.data ?? {}) as Partial<VisitorDetail>;
        setDetail({ visitor: d.visitor ?? null, events: arr(d.events) });
      })
      .catch((err) => !cancelled && setError(toLoadError(err, 'Could not load this visitor')));
    return () => {
      cancelled = true;
    };
  }, [visitorId]);

  if (error) return error.kind === 'missing' ? <Empty>No timeline for this visitor.</Empty> : <ErrorBox message={error.message} />;
  if (!detail) return <p className="py-4 text-sm text-muted-foreground">Loading timeline…</p>;
  if (detail.events.length === 0) return <Empty>No events recorded.</Empty>;
  return (
    <ol className="max-h-[480px] space-y-1 overflow-auto">
      {detail.events.map((e, i) => (
        <li key={`${e.ts}-${i}`} className="grid grid-cols-[9rem_8rem_1fr] gap-3 text-xs">
          <span className="text-muted-foreground tabular-nums">{when(e.ts)}</span>
          <span>
            <Badge variant="outline" className="font-mono">{e.event}</Badge>
          </span>
          <span className="min-w-0 break-all">
            <span className="font-mono">{e.path ?? ''}</span>
            {e.channel && <span className="ml-2 text-muted-foreground">({CHANNEL_LABEL[e.channel] ?? e.channel})</span>}
            {e.props != null && typeof e.props === 'object' && Object.keys(e.props as object).length > 0 && (
              <span className="ml-2 text-muted-foreground">{JSON.stringify(e.props)}</span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

function Visitors({ days, includeInternal, reloadKey }: { days: number; includeInternal: boolean; reloadKey: number }) {
  const [rows, setRows] = useState<VisitorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get('/admin/web-analytics/visitors', { params: { days, ...(includeInternal ? { includeInternal: '1' } : {}) } })
      .then((r) => !cancelled && setRows(arr<VisitorRow>(r.data)))
      .catch((err) => !cancelled && setError(toLoadError(err, 'Could not load visitors')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days, includeInternal, reloadKey]);

  if (error?.kind === 'missing') return <MissingApi />;
  return (
    <Card>
      <CardContent className="pt-6">
        {error && <ErrorBox message={error.message} />}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Visitor</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>First touch</TableHead>
              <TableHead className="text-right">Page views</TableHead>
              <TableHead>Last seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((v) => (
              <Fragment key={v.visitorId}>
                <TableRow
                  className="cursor-pointer"
                  onClick={() => setOpen((id) => (id === v.visitorId ? null : v.visitorId))}
                  aria-expanded={open === v.visitorId}
                >
                  <TableCell className="text-xs">
                    {v.email ? (
                      <span className="font-medium">{v.email}</span>
                    ) : (
                      <span className="font-mono" title={v.visitorId}>{v.visitorId.slice(0, 8)}</span>
                    )}
                    {v.kind && <Badge variant="secondary" className="ml-2">{v.kind}</Badge>}
                    {v.userId && <Badge variant="outline" className="ml-1">user</Badge>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {v.org ?? '—'}
                    {v.domain && <span className="ml-1 text-muted-foreground">{v.domain}</span>}
                  </TableCell>
                  <TableCell className="text-xs">{v.country ?? '—'}</TableCell>
                  <TableCell className="text-xs"><ChannelLabel channel={v.channel} /></TableCell>
                  <TableCell className="text-xs">
                    {touchText(v.firstTouch)}
                    {v.firstTouch?.lp && <span className="ml-1 font-mono text-muted-foreground">{v.firstTouch.lp}</span>}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">{fmt(v.pageViews)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{when(v.lastSeen)}</TableCell>
                </TableRow>
                {open === v.visitorId && (
                  <TableRow>
                    <TableCell colSpan={7} className="bg-muted/40">
                      <div className="mb-2 text-xs text-muted-foreground">
                        First seen {when(v.firstSeen)} · <span className="font-mono">{v.visitorId}</span>
                      </div>
                      <VisitorTimeline visitorId={v.visitorId} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
            {!loading && rows.length === 0 && !error && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  No visitors in this range.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const RANGES = [7, 30, 90] as const;

function AdminWebAnalytics() {
  const [days, setDays] = useState<number>(30);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async (d: number, internal: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/admin/web-analytics/summary', {
        params: { days: d, ...(internal ? { includeInternal: '1' } : {}) },
      });
      setSummary(normalizeSummary(r.data));
    } catch (err) {
      setError(toLoadError(err, 'Could not load website analytics'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days, includeInternal);
  }, [load, days, includeInternal, reloadKey]);

  const range = summary?.range;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Website Analytics</h1>
          <p className="text-muted-foreground">
            4klabs.ai traffic, channels and leads, plus app sign-ups — first-party, counted with or without cookies.
            {range && (
              <span className="ml-1">
                {shortDay(range.from)} – {shortDay(range.to)}.
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
          <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="visitors">Visitors</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap items-center gap-4">
            <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Date range">
              {RANGES.map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={days === d ? 'secondary' : 'ghost'}
                  aria-pressed={days === d}
                  onClick={() => setDays(d)}
                >
                  {d} days
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Switch id="wa-internal" checked={includeInternal} onCheckedChange={setIncludeInternal} />
              <Label htmlFor="wa-internal" className="text-sm">Include internal traffic</Label>
            </div>
          </div>
        </div>

        <TabsContent value="overview">
          {error?.kind === 'missing' ? (
            <MissingApi />
          ) : error ? (
            <ErrorBox message={error.message} />
          ) : !summary ? (
            <div className="flex min-h-[400px] items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className={cn(loading && 'opacity-60 transition-opacity')}>
              <Overview s={summary} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="visitors">
          <Visitors days={days} includeInternal={includeInternal} reloadKey={reloadKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
